import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';
import PacientesFilters from './components/PacientesFilters';
import PacienteListRow from './components/PacienteListRow';
import { usePacientesFilters } from '../../hooks/usePacientesFilters';
import { usePatientImageCache } from '../../hooks/usePatientImageCache';
import './PacientesBeta.css';

const PAGE_SIZE = 20;
const CACHE_KEY_PREFIX = 'pacientes_beta_cache_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

const getCacheKey = (filters) => {
  const { searchTerm, statusFilter, modalityFilter, shiftFilter } = filters;
  return `${searchTerm || ''}_${statusFilter || ''}_${modalityFilter || ''}_${shiftFilter || ''}`;
};

const loadDataCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + cacheKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.timestamp && Date.now() - data.timestamp < CACHE_TTL_MS) {
      return data;
    }
    sessionStorage.removeItem(CACHE_KEY_PREFIX + cacheKey);
  } catch (e) {
    console.warn('Cache pacientes beta:', e);
  }
  return null;
};

const saveDataCache = (cacheKey, payload) => {
  try {
    sessionStorage.setItem(
      CACHE_KEY_PREFIX + cacheKey,
      JSON.stringify({ ...payload, timestamp: Date.now() })
    );
  } catch (e) {
    // quota ou modo privado
  }
};

const PacientesBeta = () => {
  const { userAPIData } = useUserAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const { filters, updateFilter, setFilters, resetFilters } = usePacientesFilters();
  const { searchInput, searchTerm, statusFilter, modalityFilter, shiftFilter } = filters;
  const { readCacheEntry, getImageWithCache } = usePatientImageCache();
  const scrollWrapRef = useRef(null);

  const searchTimeoutRef = useRef(null);
  const [modalidades, setModalidades] = useState([]);
  const [enabledOnly] = useState(false);

  const [pacientes, setPacientes] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const abortRef = useRef(null);
  const sentinelRef = useRef(null);

  const handleSearchChange = (value) => {
    updateFilter('searchInput', value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => updateFilter('searchTerm', value), 500);
  };

  const handleSearchSubmit = () => updateFilter('searchTerm', searchInput);

  const handleClearFilters = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    resetFilters();
    setPage(0);
  };

  const cacheKey = getCacheKey(filters);

  const fetchPage = useCallback(
    async (pageNum, append = false) => {
      const baseUrl = `patient/${pageNum}/${PAGE_SIZE}`;
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('name', searchTerm);
      if (statusFilter) queryParams.append('statusId', statusFilter);
      if (modalityFilter) queryParams.append('modalityId', modalityFilter);
      if (shiftFilter) queryParams.append('shift', shiftFilter);
      if (enabledOnly) queryParams.append('enabledOnly', 'true');
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

      const controller = new AbortController();
      abortRef.current = controller;

      const response = await authFetch(fullUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Erro ao buscar pacientes');
      const data = await response.json();
      const content = Array.isArray(data.content) ? data.content : [];
      const total = Number(data.totalElements) || 0;
      const totalPgs = Number(data.totalPages) || 0;

      const enriched = content.map((p) => ({
        ...p,
        imageUrl: readCacheEntry(p.id) || null,
      }));

      return {
        list: enriched,
        totalElements: total,
        totalPages: totalPgs,
        page: pageNum,
      };
    },
    [searchTerm, statusFilter, modalityFilter, shiftFilter, enabledOnly, readCacheEntry]
  );

  // Carregar primeira página (ou restaurar do cache)
  useEffect(() => {
    const key = cacheKey;
    const cached = loadDataCache(key);
    if (cached && cached.pacientes && cached.pacientes.length > 0 && page === 0) {
      setPacientes(cached.pacientes);
      const nextToFetch = cached.nextPage ?? 1;
      setPage(Math.max(0, nextToFetch - 1)); // última página já carregada
      setTotalPages(cached.totalPages ?? 0);
      setTotalElements(cached.totalElements ?? 0);
      setHasMore(nextToFetch < (cached.totalPages ?? 0));
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPage(0, false)
      .then((res) => {
        if (cancelled) return;
        setPacientes(res.list);
        setPage(0);
        setTotalPages(res.totalPages);
        setTotalElements(res.totalElements);
        setHasMore(0 < res.totalPages - 1 || res.list.length < PAGE_SIZE);
        setLoading(false);
        saveDataCache(key, {
          pacientes: res.list,
          nextPage: 1,
          totalPages: res.totalPages,
          totalElements: res.totalElements,
        });
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [cacheKey]);

  // Quando filtros mudam, resetar e recarregar
  const prevCacheKeyRef = useRef(cacheKey);
  useEffect(() => {
    if (prevCacheKeyRef.current !== cacheKey) {
      prevCacheKeyRef.current = cacheKey;
      setPage(0);
      setPacientes([]);
      setHasMore(false);
    }
  }, [cacheKey]);

  // Infinite scroll: carregar mais ao chegar no sentinel
  const pageRef = useRef(page);
  pageRef.current = page;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        const currentPage = pageRef.current;
        const next = currentPage + 1;
        if (next >= totalPages) {
          setHasMore(false);
          return;
        }
        setLoadingMore(true);
        fetchPage(next, true)
          .then((res) => {
            setPacientes((prev) => {
              const nextList = [...prev, ...res.list];
              saveDataCache(cacheKeyRef.current, {
                pacientes: nextList,
                nextPage: next + 1,
                totalPages: res.totalPages,
                totalElements: res.totalElements,
              });
              return nextList;
            });
            setPage(next);
            setTotalPages(res.totalPages);
            setTotalElements(res.totalElements);
            setHasMore(next < res.totalPages - 1 || res.list.length === PAGE_SIZE);
          })
          .catch(() => setHasMore(false))
          .finally(() => setLoadingMore(false));
      },
      { rootMargin: '100px', threshold: 0 }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, totalPages, cacheKey, fetchPage]);

  useEffect(() => {
    const fetchModalidades = async () => {
      try {
        const res = await authFetch('modality');
        if (!res.ok) return;
        const data = await res.json();
        setModalidades(data || []);
      } catch (err) {
        console.error('Erro ao carregar modalidades:', err);
      }
    };
    fetchModalidades();
  }, []);

  const defaultImageUrl = require('../../assets/img/perfilvazio.png');
  const returnScrollToId = location.state?.returnScrollToId;

  // Rolagem até o paciente ao voltar do perfil/edição
  useEffect(() => {
    if (!returnScrollToId || !pacientes.length || loading) return;
    const inList = pacientes.some((p) => p.id === returnScrollToId);
    if (!inList) return;
    const timer = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-paciente-id="${returnScrollToId}"]`);
      if (el && scrollWrapRef.current) {
        el.scrollIntoView({ block: 'nearest' });
      }
      navigate(location.pathname, { replace: true, state: {} });
    });
    return () => cancelAnimationFrame(timer);
  }, [returnScrollToId, pacientes, loading, navigate, location.pathname]);

  if (error) {
    return (
      <div className="content">
        <p className="text-danger">Erro: {error}</p>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="pacientes-beta">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h3 className="mb-0">Pacientes</h3>
            <span className="pacientes-beta__badge pacientes-beta__badge--beta">beta</span>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Link to="/Pacientes/Novo" className="btn btn-info btn-sm">
              Cadastrar Paciente
            </Link>
            {userAPIData?.userRoleId === 1 && (
              <>
                <Link to="/Planos" className="btn btn-info btn-sm">
                  Gerenciar Planos
                </Link>
                <Link to="/salas" className="btn btn-info btn-sm">
                  Gerenciar Salas
                </Link>
              </>
            )}
          </div>
        </div>

        <PacientesFilters
          searchInput={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          statusFilter={statusFilter}
          onStatusChange={(v) => updateFilter('statusFilter', v)}
          modalityFilter={modalityFilter}
          onModalityChange={(v) => updateFilter('modalityFilter', v)}
          shiftFilter={shiftFilter}
          onShiftChange={(v) => updateFilter('shiftFilter', v)}
          modalidades={modalidades}
          onClearFilters={handleClearFilters}
        />

        <div className="pacientes-beta__scroll-wrap" ref={scrollWrapRef}>
          <div className="position-relative">
            {loading && (
            <div
              className="position-absolute top-0 start-0 w-100 d-flex align-items-center justify-content-center py-5"
              style={{ backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 2 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          )}

          <ul
            className={`pacientes-beta__list ${loading ? 'opacity-25' : ''}`}
            style={{ pointerEvents: loading ? 'none' : undefined }}
          >
            {pacientes.length > 0 && !loading && (
              <li className="pacientes-beta__list-header" aria-hidden="true">
                <span className="pacientes-beta__list-header-cell pacientes-beta__list-header-cell--avatar">Foto</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__list-header-cell--nome">Paciente</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__col-header">Idade</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__col-header">Turno</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__col-header">Modalidade</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__col-header">Status</span>
                <span className="pacientes-beta__list-header-cell pacientes-beta__list-header-cell--chevron" />
              </li>
            )}
            {pacientes.length === 0 && !loading && (
              <li className="pacientes-beta__empty">
                Nenhum paciente encontrado.
              </li>
            )}
            {pacientes.map((paciente) => (
              <li key={paciente.id} data-paciente-id={paciente.id}>
                <PacienteListRow
                  paciente={paciente}
                  defaultImageUrl={defaultImageUrl}
                  onLoadImage={getImageWithCache}
                  modalityName={modalidades.find((m) => m.id === paciente.modalityId)?.name}
                />
              </li>
            ))}
            <li ref={sentinelRef} className="pacientes-beta__sentinel" aria-hidden="true" />
          </ul>

          {loadingMore && (
            <div className="pacientes-beta__loading-more d-flex justify-content-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando mais...</span>
              </div>
            </div>
          )}

          {!loading && pacientes.length > 0 && (
            <div className="text-center text-secondary small mt-2">
              {totalElements > 0 && (
                <>Exibindo {pacientes.length} de {totalElements} pacientes</>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PacientesBeta;
